import { useState, useEffect, useCallback } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { adminBrandsService, AdminBrand } from "../../services/admin/brands.admin.service";
import { adminService } from "../../services/admin/admin.service";
import { DataTable, DataTablePagination } from "../../components/ui/data-table";
import { Building2, Mail, Search, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { Switch } from "@headlessui/react";

export default function BrandEmailAutomation() {
  const [brands, setBrands] = useState<AdminBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [updatingBrandId, setUpdatingBrandId] = useState<string | null>(null);

  // Fetch brands
  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: pageSize,
      };

      if (searchQuery) {
        params.keyword = searchQuery;
        params.searchBy = "COMPANY";
      }

      const response = await adminBrandsService.searchBrands(params);
      setBrands(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleToggleEmailAutomation = async (brandId: string, enabled: boolean) => {
    setUpdatingBrandId(brandId);
    try {
      await adminService.toggleBrandEmailAutomation(brandId, enabled);
      toast.success(`Email automation ${enabled ? "enabled" : "disabled"} successfully`);
      
      // Update local state
      setBrands(brands.map(brand => 
        brand.id === brandId 
          ? { ...brand, isEmailAutomationEnabled: enabled }
          : brand
      ));
    } catch (error) {
      console.error("Error toggling email automation:", error);
      toast.error("Failed to update email automation");
    } finally {
      setUpdatingBrandId(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchBrands();
  };

  const columns: ColumnDef<AdminBrand>[] = [
    {
      id: "brand",
      header: "Brand",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 shrink-0 rounded-full bg-muted flex items-center justify-center overflow-hidden">
            {row.original.brandProfileImage ? (
              <img
                src={row.original.brandProfileImage}
                alt={row.original.companyName || "Brand"}
                className="h-full w-full object-cover"
              />
            ) : (
              <Building2 className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <span className="font-medium text-foreground truncate">
            {row.original.companyName || "Unnamed Brand"}
          </span>
        </div>
      ),
      meta: {
        width: "w-64",
        skeleton: (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            <div className="h-3.5 w-32 rounded bg-muted animate-pulse" />
          </div>
        ),
      },
    },
    {
      id: "website",
      header: "Website",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground truncate">
          {row.original.website || "-"}
        </span>
      ),
    },
    {
      id: "email",
      header: "Email",
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{row.original.email}</span>
      ),
    },
    {
      id: "joinedAt",
      header: "Joined Date",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {format(new Date(row.original.joinedAt), "MMM d, yyyy")}
        </span>
      ),
      meta: { fit: true },
    },
    {
      id: "emailAutomation",
      header: "Email Automation",
      cell: ({ row }) => (
        <div className="flex items-center justify-center gap-2">
          <Switch
            checked={row.original.isEmailAutomationEnabled || false}
            onChange={(enabled) =>
              handleToggleEmailAutomation(row.original.id, enabled)
            }
            disabled={updatingBrandId === row.original.id}
            className={`${
              row.original.isEmailAutomationEnabled ? "bg-[#d7785b]" : "bg-muted"
            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span
              className={`${
                row.original.isEmailAutomationEnabled
                  ? "translate-x-6"
                  : "translate-x-1"
              } inline-block h-4 w-4 transform rounded-full bg-card transition-transform`}
            />
          </Switch>
          {updatingBrandId === row.original.id && (
            <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
          )}
        </div>
      ),
      meta: {
        width: "w-40",
        headerClassName: "text-center",
        skeleton: (
          <div className="flex justify-center">
            <div className="h-6 w-11 rounded-full bg-muted animate-pulse" />
          </div>
        ),
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Mail className="h-7 w-7 text-[#d7785b]" />
            Brand Email Automation
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage email automation feature access for brands
          </p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="bg-card border border-border rounded-xl p-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by company name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-[#d7785b] text-white rounded-lg hover:bg-[#c56a4f] transition-colors font-medium"
          >
            Search
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={brands}
          isLoading={loading}
          skeletonRows={5}
          getRowId={(row) => row.id}
          emptyText={
            <span className="inline-block">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
              <span className="block text-lg font-medium">No brands found</span>
              <span className="block text-sm mt-1">
                Try adjusting your search criteria
              </span>
            </span>
          }
        />

        {/* Pagination */}
        {!loading && brands.length > 0 && (
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={setPage}
            noun="brands"
          />
        )}
      </div>
    </div>
  );
}

